/**
 * Authentication Utilities
 * Centralized authentication logic for consistent API calls
 */

import { API_BASE_URL } from '../config';

// Storage keys
const STORAGE_KEYS = {
  USER_DATA: 'user',
  ADMIN_TOKEN: 'admin_token',
  SESSION_TOKEN: 'session_token'
};

// In-memory token — survives navigation but not page refresh.
// Keeps the token out of localStorage (XSS risk) while bypassing
// cross-origin cookie blocking.
let _inMemoryToken = null;

const SESSION_TOKEN_KEY = 'cloudcor_session_token';

export function setInMemoryToken(token) {
  _inMemoryToken = token || null;
  // Persist to both sessionStorage (fast same-tab access) and localStorage
  // (survives new tabs and browser restarts within the same session).
  try {
    if (token) {
      sessionStorage.setItem(SESSION_TOKEN_KEY, token);
      localStorage.setItem(SESSION_TOKEN_KEY, token);
    } else {
      sessionStorage.removeItem(SESSION_TOKEN_KEY);
      localStorage.removeItem(SESSION_TOKEN_KEY);
    }
  } catch (_) {
    // storage unavailable — in-memory only
  }
}

/**
 * Get authentication headers for API requests
 * Supports both Bearer token and session cookie authentication
 */
export function getAuthHeaders() {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };

  // Always use the session token from getAdminToken
  const sessionToken = getAdminToken();
  if (sessionToken) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
    return headers;
  }

  // Fallback: no Authorization header
  return headers;
}

/**
 * Get fetch options with authentication
 */
export function getAuthFetchOptions(options = {}) {
  return {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...(options.headers || {})
    },
    credentials: 'include' // Include cookies for session auth fallback
  };
}

/**
 * Get admin token from storage
 */
export function getAdminToken() {
  if (_inMemoryToken) return _inMemoryToken;
  try {
    // Check sessionStorage first (fastest for same-tab access)
    const fromSession = sessionStorage.getItem(SESSION_TOKEN_KEY);
    if (fromSession) {
      _inMemoryToken = fromSession;
      return fromSession;
    }
    // Fall back to localStorage — survives new tabs and browser restarts
    const fromLocal = localStorage.getItem(SESSION_TOKEN_KEY);
    if (fromLocal) {
      _inMemoryToken = fromLocal;
      return fromLocal;
    }
  } catch (_) {
    // storage unavailable
  }
  return null;
}

/**
 * Get user token from storage
 */
export function getUserToken() {
  try {
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    if (userData) {
      const user = JSON.parse(userData);
      return user.token;
    }
    return null;
  } catch (error) {
    console.error('Error getting user token:', error);
    return null;
  }
}

/**
 * Store admin user data (without token — the HttpOnly session cookie carries the session).
 */
export function storeAdminToken(_token, userData = null) {
  try {
    // Do NOT persist the token to localStorage to prevent XSS token theft.
    // The backend sets an HttpOnly Secure cookie; rely on that for session continuity.
    if (userData) {
      // Strip any token fields before persisting
      // eslint-disable-next-line no-unused-vars
      const { token, sessionToken, adminToken, ...safeData } = userData;
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify({ ...safeData, isAdmin: true }));
    }
  } catch (error) {
    console.error('Error storing admin user data:', error);
  }
}

/**
 * Clear all authentication tokens
 */
export function clearAuthTokens() {
  try {
    _inMemoryToken = null;
    sessionStorage.removeItem(SESSION_TOKEN_KEY);
    localStorage.removeItem(SESSION_TOKEN_KEY);
    localStorage.removeItem(STORAGE_KEYS.ADMIN_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    localStorage.removeItem(STORAGE_KEYS.SESSION_TOKEN);
  } catch (error) {
    console.error('Error clearing auth tokens:', error);
  }
}

/**
 * Check if user is authenticated with admin privileges
 */
export function isAdminAuthenticated() {
  const adminToken = getAdminToken();
  return !!adminToken;
}

/**
 * Make authenticated API request
 */
export async function makeAuthenticatedRequest(url, options = {}) {
  const requestOptions = getAuthFetchOptions(options);
  
  try {
    const response = await fetch(url, requestOptions);
    
    // Handle 401 responses by clearing tokens and redirecting
    if (response.status === 401) {
      console.warn('Authentication failed, clearing tokens');
      clearAuthTokens();
      
      // Only redirect if we're not already on the login page
      if (!window.location.pathname.includes('/manual-login')) {
        window.location.href = '/manual-login';
      }
      
      throw new Error('Authentication required');
    }
    
    return response;
  } catch (error) {
    console.error('Authenticated request failed:', error);
    throw error;
  }
}

/**
 * Admin login function with proper token storage
 */
export async function adminLogin(password) {
  try {
    // Send only the password as expected by the backend API
    const requestBody = { password };
    
    const response = await fetch(`${API_BASE_URL}/admin/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody),
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error('Login failed');
    }

    const result = await response.json();
    
    if (result.valid) {
      if (result.token) setInMemoryToken(result.token);
      storeAdminToken(null, result.user);
      return { success: true, user: result.user };
    }

    return { success: false, error: 'Invalid credentials' };
  } catch (error) {
    console.error('Admin login error:', error);
    return { success: false, error: error.message };
  }
} 